import React from 'react';
import { useParams } from 'react-router-dom';
import { LanguagePicker } from '../components/LanguagePicker';
import { Button, Text } from '../components/ds';
import { useLanguage } from '../i18n';

const InstallAppPage: React.FC = () => {
  const { ping_id } = useParams<{ ping_id?: string }>();
  const { t } = useLanguage();

  // No automatic re-navigation here. The OS intercepts the *original* tap on
  // the https://iamok.live/ping/... link (from the email or a message) before
  // this page ever loads. Once we are rendering, the handoff has already been
  // declined — iOS deliberately does not honour a Universal Link for a
  // same-domain navigation started by the page's own JavaScript, so replacing
  // location with this same URL cannot open the app. It only reloads us, which
  // is an infinite loop.
  //
  // The custom scheme below is the one escape hatch that does still work, so it
  // is offered as an explicit user action rather than fired automatically.
  const openInApp = () => {
    if (!ping_id) return;
    window.location.href = `iamok://ping/${ping_id}`;
  };

  return (
    <div className="min-h-screen bg-surface-bg flex flex-col">
      <header className="w-full p-4 sm:p-6 flex justify-between items-center border-b border-gray-200 bg-surface-card">
        <Text as="h1" variant="heading" className="text-brand-primary">
          iam.ok
        </Text>
        <LanguagePicker />
      </header>

      <main className="flex-1 max-w-xl w-full mx-auto px-4 py-12 sm:py-20 flex flex-col items-center text-center gap-6">
        <Text as="h2" variant="display">
          {ping_id ? t('install.heading.ping') : t('install.heading.default')}
        </Text>
        <Text variant="body-lg" color="muted">
          {ping_id ? t('install.body.ping') : t('install.body.default')}
        </Text>

        <div className="flex flex-col sm:flex-row gap-3 mt-4 w-full sm:w-auto">
          <a
            href="https://apps.apple.com/app/id0000000000"
            className="inline-flex items-center justify-center"
            aria-label={t('install.appStore.aria')}
          >
            <span className="rounded-full bg-brand-primary text-white px-5 py-3 text-body-lg font-semibold">
              {t('install.appStore.label')}
            </span>
          </a>
          <a
            href="https://play.google.com/store/apps/details?id=live.iamok.app"
            className="inline-flex items-center justify-center"
            aria-label={t('install.googlePlay.aria')}
          >
            <span className="rounded-full bg-brand-primary text-white px-5 py-3 text-body-lg font-semibold">
              {t('install.googlePlay.label')}
            </span>
          </a>
        </div>

        {ping_id ? (
          <Button variant="ghost" size="md" onClick={openInApp}>
            {t('install.openInApp')}
          </Button>
        ) : null}

        <div className="mt-12 max-w-md">
          <Text variant="caption" color="muted">
            {t('install.monitorNote')}
          </Text>
        </div>
      </main>

      <footer className="py-6 px-4 text-center border-t border-gray-200 bg-surface-card">
        <Text variant="caption" color="muted">
          {t('common.footer.copyright', { year: new Date().getFullYear() })}
        </Text>
      </footer>
    </div>
  );
};

export default InstallAppPage;
